// pages/settings.js — Settings page.
//
// Owns the `data-page="settings"` section. Forks on deploy mode:
//   - local-daemon: renderDaemonSettings (lease TTL, log level,
//     worktree allocation, etc.)
//   - cloud / coordinator-hosted: team connection panel + My
//     Machines + Billing + API Tokens
//
// Each subrender is its own function in dashboard.js and stays
// there. We only migrate the entry-point dispatch.
//
// Per Worktrees bug-sweep finding: no initial render in start() —
// legacy code handles activePage change.
import { $ } from "../dom.js";
import { escapeHTML } from "../util.js";
import { teamState } from "../page-state.js";
import { renderMyMachines } from "../my-machines.js";
import { renderApiTokens } from "../api-tokens.js";
import { renderBusPanel, wireBuCreate } from "../team-bus.js";
import { renderReposPanel } from "../team-repos.js";
import { fetchTeamInfo } from "../fetchers.js";
import {
  renderDaemonSettings,
  updateDecryptStatus,
} from "../daemon-settings.js";

// One-time wire for the workspace create-form's submit/cancel
// listeners. Module-scoped because the Settings page is static-render
// (no tick re-renders that would otherwise stack duplicate listeners),
// but defensive against future changes that might add tick refresh.
let buCreateWired = false;

// All 5 settings renderers are now direct imports (Tier 2n covered
// 3, Tier 2q covered the last 2). pages/settings.js no longer reads
// AC_LEGACY.
async function renderSettingsPage() {

  // PR #9 unification: when running in daemon mode, populate the
  // daemon-only settings panels (lease TTL, log level, worktree
  // allocation, etc.). The cloud panels above (Team connection,
  // My Machines, Billing) are CSS-hidden in daemon mode so their
  // renderers would no-op anyway, but skipping the network calls
  // saves a 404 on /v1/teams/info etc.
  const isDaemon =
    window.AC_CONFIG && window.AC_CONFIG.DEPLOY_MODE === "local-daemon";
  if (isDaemon) {
    renderDaemonSettings();
    return;
  }

  // Cloud-pages / coordinator-hosted: prime teamState.info so both
  // the Team-connection panel below AND the Workspaces panel render
  // with correct authz on first paint. Without the prefetch, a user
  // landing on /ui/settings as their first nav (no prior Team page
  // visit) would see canManageBUs=false even as an admin, and the
  // "+ New workspace" button would stay hidden until the global
  // tick fills teamState. fetchTeamInfo is the same call this would
  // have made (api("/v1/teams/info")), and is idempotent + cached.
  await fetchTeamInfo();
  const info = teamState.info;
  if (info) {
    if ($("settings-account-team")) {
      $("settings-account-team").innerHTML =
        escapeHTML(info.team_name || "") +
        ' <span style="color: var(--text-tertiary); font-size: 12px;">(' +
        escapeHTML(info.your_role || "") +
        ")</span>";
    }
  } else if ($("settings-account-team")) {
    $("settings-account-team").textContent = "Not linked";
  }

  // Workspaces panel (PR #131 — relocated from Team page).
  // Owner role retired in migration 035 — your_team_role === "owner"
  // retained for back-compat with JWTs minted pre-migration.
  const canManageBUs = !!(info && (
    info.your_role === "admin"
    || info.your_team_role === "admin"
    || info.your_team_role === "owner"
  ));
  renderBusPanel(canManageBUs);
  if (!buCreateWired) {
    wireBuCreate();
    buCreateWired = true;
  }

  // Repos → Workspaces panel (PR F, 2026-05-15). Renders next to
  // the Workspaces panel; auto-hides if the tenant has no repos
  // in its allow-list yet (typical for tenants pre-GitHub-App-
  // install). canManage controls whether the per-row Workspace
  // dropdown is interactive.
  renderReposPanel(canManageBUs);

  // Decryption status
  updateDecryptStatus();

  // Billing moved to its own top-level Billing tab (pages/billing.js) on
  // 2026-06-23 — no longer rendered here.
  renderApiTokens();
  renderMyMachines();
}

// ---- mount ----
//
// Settings re-render via dashboard:tick would re-fetch
// /v1/teams/info every poll cycle (5s) and tear down input state
// the user might be typing in. Settings is a static-ish page in
// dashboard.js's classification — renderActivePage's force-render
// path only re-runs it on explicit navigation, NOT on poll tick.
// We mirror that: only render on navigate (MutationObserver),
// NOT on dashboard:tick. The legacy renderSettingsPage's same-tick
// short-circuit happens because renderActivePage gates static
// pages behind force=true; without subscribing to dashboard:tick
// we get the same behavior here.

let started = false;

function start() {
  if (started) return;
  started = true;
  // Settings IS the one place we DO want the initial render in
  // start() — because dashboard.js's renderActivePage skips
  // settings on normal tick (the page is in the "static-ish"
  // category there), so the legacy fallback that other pages rely
  // on doesn't fire here. We need to render at least once to
  // populate the panels.
  renderSettingsPage();
}

function stop() {
  if (!started) return;
  started = false;
}

function bindLifecycle() {
  const section = document.querySelector('section.page[data-page="settings"]');
  if (!section) return;
  if (section.classList.contains("active")) start();
  const obs = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      if (m.attributeName !== "class") return;
      if (section.classList.contains("active")) start();
      else stop();
    });
  });
  obs.observe(section, { attributes: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bindLifecycle);
} else {
  bindLifecycle();
}
